import React, { useEffect, useRef, useState } from 'react';
import Konva from 'konva';
import { Image, Layer, Line, Rect, Stage, Transformer } from 'react-konva';
import { TGDComponent, TGDDesign } from '../../../models/courses/graphicDesign';
import { componentBox, fromNodeTransform } from '../core/geometry';
import { snapBox, TGuide } from '../core/snapping';
import GDNode from './GDNode';
import GDTextEditor from './GDTextEditor';
import { useFitScale } from './useFitScale';
import { useGDFonts } from './useGDFonts';
import { useHtmlImage } from './useHtmlImage';

export interface GDStageProps {
  design: TGDDesign; artboard: { width: number; height: number };
  selectedId: string | null; readOnly?: boolean; zoom: number | 'fit';
  direction: 'ltr' | 'rtl'; onSelect: (id: string | null) => void;
  onPreview: (id: string, patch: Partial<TGDComponent>) => void;
  onCommit: (id: string, patch: Partial<TGDComponent>) => void;
  onStageReady?: (stage: Konva.Stage | null) => void;
  overlay?: React.ReactNode;
  backgroundImage?: string;
}

const GDStage: React.FC<GDStageProps> = ({ design, artboard, selectedId, readOnly,
  zoom, direction, onSelect, onPreview, onCommit, onStageReady,
  overlay, backgroundImage }) => {
  const container = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const [guides, setGuides] = useState<TGuide[]>([]);
  const [editing, setEditing] = useState<{ component: TGDComponent; node: Konva.Text } | null>(null);
  const transformBase = useRef<TGDComponent | null>(null);
  const { scale, width, height } = useFitScale(container, artboard, zoom);
  const fontsVersion = useGDFonts(design);
  const background = useHtmlImage(backgroundImage);
  const selected = design.components.find(c => c.id === selectedId);
  useEffect(() => {
    const stage = stageRef.current;
    const transformer = transformerRef.current;
    if (!stage || !transformer) return;
    const node = selectedId ? stage.findOne(`#${selectedId}`) : null;
    transformer.nodes(node && !readOnly ? [node] : []);
    transformer.getLayer()?.batchDraw();
  }, [selectedId, design.components, readOnly]);
  useEffect(() => {
    onStageReady?.(stageRef.current);
    return () => onStageReady?.(null);
  }, [onStageReady]);
  useEffect(() => { stageRef.current?.batchDraw(); }, [fontsVersion]);
  const snap = (component: TGDComponent, node: Konva.Node) => {
    const box = { ...componentBox(component), x: node.x() - component.width / 2,
      y: node.y() - component.height / 2 };
    const others = design.components.filter(c => c.id !== component.id).map(componentBox);
    const result = snapBox(box, others, artboard, { threshold: 6 / scale, grid: 8 });
    node.position({ x: result.x + component.width / 2, y: result.y + component.height / 2 });
    setGuides(result.guides);
  };
  const anchors = selected?.type === 'text'
    ? ['top-left', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-right']
    : undefined;
  return <div ref={container} className="gd-stage-wrap" dir={direction}>
    <Stage ref={stageRef} width={width} height={height} scaleX={scale} scaleY={scale}
      onMouseDown={event => { if (event.target === event.target.getStage()) onSelect(null); }}
      onTouchStart={event => { if (event.target === event.target.getStage()) onSelect(null); }}>
      <Layer listening={false}><Rect width={artboard.width} height={artboard.height}
        fill={design.background} />
        {background && <Image image={background} width={artboard.width}
          height={artboard.height} />}
      </Layer>
      <Layer>
        {[...design.components].sort((a, b) => (a.layer ?? 0) - (b.layer ?? 0)).map(component =>
          <GDNode key={component.id} component={component} fontsVersion={fontsVersion}
            draggable={!readOnly}
            onSelect={() => onSelect(component.id)}
            onPreview={patch => onPreview(component.id, patch)}
            onCommit={patch => { setGuides([]); onCommit(component.id, patch); }}
            onDragMove={node => snap(component, node)}
            onEdit={node => { if (!readOnly) setEditing({ component, node }); }} />)}
      </Layer>
      <Layer>
        {!readOnly && <Transformer ref={transformerRef} anchorSize={14}
          rotateAnchorOffset={28} borderStroke="#2d9cc9" anchorStroke="#2d9cc9"
          anchorFill="#fff" anchorCornerRadius={3} enabledAnchors={anchors}
          keepRatio={selected?.type === 'pictogram' || selected?.sides === 0}
          boundBoxFunc={(oldBox, newBox) => newBox.width < 4 || newBox.height < 4 ? oldBox : newBox}
          onTransformStart={() => { transformBase.current = selected ?? null; }}
          onTransform={() => {
            const node = transformerRef.current?.nodes()[0];
            const base = transformBase.current;
            if (!node || !base) return;
            onPreview(base.id, fromNodeTransform(base, {
              x: node.x(), y: node.y(), scaleX: node.scaleX(),
              scaleY: node.scaleY(), rotation: node.rotation(),
            }));
          }}
          onTransformEnd={() => {
            const node = transformerRef.current?.nodes()[0];
            const base = transformBase.current ?? selected;
            if (!node || !base) return;
            const patch = fromNodeTransform(base, {
              x: node.x(), y: node.y(), scaleX: node.scaleX(),
              scaleY: node.scaleY(), rotation: node.rotation(),
            });
            node.scale({ x: 1, y: 1 });
            onCommit(base.id, patch);
            transformBase.current = null;
          }} />}
        {guides.map((guide, index) => <Line key={index} stroke="#d946ef"
          dash={[6, 4]} listening={false} points={guide.orientation === 'vertical'
            ? [guide.position, 0, guide.position, artboard.height]
            : [0, guide.position, artboard.width, guide.position]} />)}
      </Layer>
      {overlay && <Layer>{overlay}</Layer>}
    </Stage>
    {editing && <GDTextEditor component={editing.component} node={editing.node} scale={scale}
      onCommit={text => onCommit(editing.component.id, { text })}
      onClose={() => setEditing(null)} />}
  </div>;
};

export default GDStage;
